import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const preferenceOptions = [
    { id: 'tutorials', label: 'Automation Tutorials', icon: 'BookOpen' },
    { id: 'case-studies', label: 'Case Studies', icon: 'FileText' },
    { id: 'industry-news', label: 'Industry News', icon: 'Newspaper' },
    { id: 'product-updates', label: 'Product Updates', icon: 'Zap' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubscribed(true);
    setIsSubmitting(false);
  };

  const togglePreference = (id: string) => {
    setPreferences(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  if (isSubscribed) {
    return (
      <div className="bg-gradient-to-br from-success/10 to-success/5 border border-success/20 rounded-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-success/20 rounded-full mb-4">
          <Icon name="CheckCircle" className="w-8 h-8 text-success" />
        </div>
        <h3 className="text-2xl font-bold text-text-primary mb-2">
          You're Subscribed!
        </h3>
        <p className="text-text-secondary">
          Check your inbox for a confirmation email. Welcome to the automation community!
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-border rounded-xl p-8 lg:p-12">
      <div className="max-w-2xl mx-auto text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
          <Icon name="Mail" className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-3xl font-bold text-text-primary mb-4">
          Stay Ahead of the Automation Curve
        </h3>
        <p className="text-lg text-text-secondary">
          Get weekly insights, tutorials, and exclusive automation strategies delivered to your inbox. Join 10,000+ professionals already learning.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
        <div className="mb-6">
          <Input
            type="email"
            name="newsletterEmail"
            autoComplete="email"
            label="Email Address"
            placeholder="your.email@company.com"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            required
            className="text-base"
          />
        </div>

        <div className="mb-6">
          <p className="text-sm font-medium text-text-primary mb-3">
            What interests you? (Optional)
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {preferenceOptions.map((option) => (
              <Checkbox
                key={option.id}
                label={
                  <div className="flex items-center space-x-2">
                    <Icon name={option.icon as any} className="w-4 h-4" />
                    <span>{option.label}</span>
                  </div>
                }
                name={`preference-${option.id}`}
                // also expose the grouping name for autofill if needed
                // (optionally server-side you can parse multiple pref keys)
                checked={preferences.includes(option.id)}
                onChange={() => togglePreference(option.id)}
              />
            ))}
          </div>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
        >
          Subscribe to Newsletter
        </Button>

        <p className="text-xs text-text-secondary text-center mt-4">
          By subscribing, you agree to receive marketing emails. Unsubscribe anytime.
        </p>
      </form>

      <div className="grid sm:grid-cols-3 gap-6 mt-12 pt-8 border-t border-border">
        <div className="text-center">
          <div className="text-3xl font-bold text-primary mb-1">10,000+</div>
          <p className="text-sm text-text-secondary">Active Subscribers</p>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-primary mb-1">Weekly</div>
          <p className="text-sm text-text-secondary">Expert Insights</p>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-primary mb-1">100%</div>
          <p className="text-sm text-text-secondary">Free Content</p>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSection;